import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  STORE_CARD_DESCRIPTION_DEFAULT_LINES,
  STORE_CARD_DESCRIPTION_LINE_HEIGHT_REM,
} from '@/lib/constants';
import { truncateTextToFitElement } from '@/lib/store-card-description';

type StoreCardDescriptionProps = {
  children: string;
  /** Text size/color (no padding — padding goes on wrapperClassName). */
  className?: string;
  /** Outer spacing (px/pb); kept separate so fixed line height measures text only. */
  wrapperClassName?: string;
  /** Max visible lines; box height is fixed so overflow never expands the card. */
  lines?: 1 | 2 | 3;
};

/**
 * Store card marketing blurbs: fixed-height text box (3 lines by default) + measured "..." truncation.
 */
export function StoreCardDescription({
  children,
  className,
  wrapperClassName,
  lines = STORE_CARD_DESCRIPTION_DEFAULT_LINES,
}: StoreCardDescriptionProps) {
  const ref = useRef<HTMLParagraphElement>(null);
  const fullText = children.trim();
  const [display, setDisplay] = useState(fullText);

  const heightRem = lines * STORE_CARD_DESCRIPTION_LINE_HEIGHT_REM;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const apply = () => {
      if (!fullText) {
        setDisplay('');
        return;
      }
      const next = truncateTextToFitElement(el, fullText);
      setDisplay(next);
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, [fullText, lines, heightRem]);

  if (!fullText) return null;

  return (
    <div className={cn('min-w-0', wrapperClassName)}>
      <p
        ref={ref}
        className={cn('overflow-hidden break-words leading-5', className)}
        style={{ height: `${heightRem}rem`, maxHeight: `${heightRem}rem` }}
        title={fullText}
      >
        {display}
      </p>
    </div>
  );
}
